import React, { useCallback, useState } from "react";
import {
  EuiBadge,
  EuiBasicTable,
  EuiBasicTableColumn,
  EuiButton,
  EuiButtonEmpty,
  EuiCallOut,
  EuiFlexGroup,
  EuiFlexItem,
  EuiIcon,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiSpacer,
  EuiText,
} from "@elastic/eui";
import { useApi } from "src/api";
import {
  OpsanioSyncDevicePlan,
  OpsanioSyncNetwork,
  OpsanioSyncNetworkAction,
  OpsanioSyncPlan,
  useAnketaAuth,
} from "src/auth";
import { useAnketaI18n } from "src/internationalization";
import { useToast } from "src/notifications";
import { HARDWARE_KIND_ICON } from "../hardware";

const i18n = "anketa.opsanio.appliance.flyout.hardware.sync";

/** How much of the addressing and cabling half is still outstanding. */
interface NetworkWork {
  /** Steps that would write something: what "Apply" is for. */
  pending: number;
  /** Steps NetBox already holds - a re-run is a no-op for these. */
  existing: number;
  /** Steps that cannot be applied as planned, with the reason. */
  blocked: OpsanioSyncNetworkAction[];
}

const NETWORK_GROUPS = ["cables", "lags", "addresses"] as const;

/**
 * Split the network plan into work, no-ops and blockers.
 *
 * The device half and the network half are counted separately on purpose: an
 * appliance can conform perfectly as a set of device records and still have no
 * bond, no addresses and no cable to either switch.
 */
const networkWork = (network: OpsanioSyncNetwork | undefined): NetworkWork => {
  const actions = network
    ? NETWORK_GROUPS.flatMap((group) => network[group] ?? [])
    : [];
  return {
    pending: actions.filter((entry) => entry.ok && !entry.existed).length,
    existing: actions.filter((entry) => entry.ok && entry.existed).length,
    blocked: actions.filter((entry) => !entry.ok),
  };
};

/**
 * Manual re-run of the questionnaire-save NetBox workflow for this appliance.
 *
 * The save path only syncs when a hardware selection changes, so drift (a device
 * renamed by hand, hardware picked before a convention existed, a missing tag)
 * has no repair path. This button is that repair path.
 *
 * It always previews first: the same endpoint is called with `dryRun: true`,
 * which writes nothing, and only the explicit Apply in the modal passes
 * `dryRun: false`. Renaming a production device is not something to do on a
 * mis-click.
 */
export const HardwareSyncButton = ({
  applianceName,
  onApplied,
}: {
  applianceName: string;
  /** Reload the device list so the tab shows the new names/tags. */
  onApplied: () => void;
}): JSX.Element => {
  const { Post } = useApi();
  const { keycloak } = useAnketaAuth();
  const { translate } = useAnketaI18n();
  const { addToast } = useToast();

  const [plan, setPlan] = useState<OpsanioSyncPlan | null>(null);
  const [planning, setPlanning] = useState<boolean>(false);
  const [applying, setApplying] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const call = useCallback(
    async (dryRun: boolean): Promise<OpsanioSyncPlan | null> => {
      const response = await Post<any>({
        path: `v2/netbox/appliance/${encodeURIComponent(applianceName)}/sync`,
        value: { dryRun },
        responseType: "json",
        token: keycloak?.token,
      });
      // The API client never throws on HTTP errors - `success` is the check.
      if (!response.success || response.error) {
        setError(
          response.error?.message ??
            translate(`${i18n}.error.generic`, {
              defaultMessage: "NetBox sync could not be run.",
            }),
        );
        return null;
      }
      return ((response.data as any)?.data as OpsanioSyncPlan) ?? null;
    },
    [Post, applianceName, keycloak, translate],
  );

  const preview = async () => {
    setPlanning(true);
    setError(null);
    const result = await call(true);
    setPlanning(false);
    if (result) setPlan(result);
  };

  const apply = async () => {
    setApplying(true);
    setError(null);
    const result = await call(false);
    setApplying(false);
    if (!result) return;

    const failed = result.devices.filter((device) => device.error);
    const applied = networkWork(result.network);
    addToast({
      title: translate(`${i18n}.applied.title`, {
        defaultMessage: "NetBox sync finished",
      }),
      color:
        failed.length > 0 || applied.blocked.length > 0 ? "warning" : "success",
      text: (
        <>
          <p>
            {failed.length > 0
              ? translate(`${i18n}.applied.partial`, {
                  values: {
                    failed: failed.length,
                    total: result.devices.length,
                  },
                  defaultMessage:
                    "{{failed}} of {{total}} devices failed to sync",
                })
              : translate(`${i18n}.applied.ok`, {
                  values: { total: result.devices.length },
                  defaultMessage: "{{total}} devices are now conform",
                })}
          </p>
          {result.network && (
            <p>
              {translate(`${i18n}.applied.network`, {
                values: {
                  written: applied.pending + applied.existing,
                  blocked: applied.blocked.length,
                },
                defaultMessage:
                  "{{written}} addressing/cabling steps in place, {{blocked}} blocked",
              })}
            </p>
          )}
        </>
      ),
    });
    setPlan(null);
    onApplied();
  };

  const changeColumns: Array<EuiBasicTableColumn<OpsanioSyncDevicePlan>> = [
    {
      name: translate(`${i18n}.column.device`, { defaultMessage: "Device" }),
      render: (device: OpsanioSyncDevicePlan) => (
        <EuiFlexGroup gutterSize="xs" alignItems="center" responsive={false}>
          <EuiFlexItem grow={false}>
            <EuiIcon type={HARDWARE_KIND_ICON[device.kind]} size="m" />
          </EuiFlexItem>
          <EuiFlexItem>
            <EuiText size="s">
              <strong>{device.currentName || `#${device.deviceId}`}</strong>
            </EuiText>
            <EuiText size="xs" color="subdued">
              {device.serial
                ? `SN: ${device.serial}`
                : translate(`${i18n}.noSerial`, {
                    defaultMessage: "no serial number",
                  })}
            </EuiText>
          </EuiFlexItem>
        </EuiFlexGroup>
      ),
    },
    {
      name: translate(`${i18n}.column.changes`, { defaultMessage: "Changes" }),
      render: (device: OpsanioSyncDevicePlan) => {
        if (device.changes.length === 0) {
          return (
            <EuiText size="xs" color="subdued">
              {device.warning ?? (
                <EuiBadge color="success">
                  {translate(`${i18n}.conform`, {
                    defaultMessage: "already conform",
                  })}
                </EuiBadge>
              )}
            </EuiText>
          );
        }
        return (
          <>
            {device.changes.map((change) =>
              // Tags are only ever added - rendering them as "old → new" would
              // wrongly suggest the device's other tags get dropped.
              change.field === "tags" ? (
                <EuiText size="xs" key={change.field}>
                  <strong>{change.field}</strong>:{" "}
                  <EuiBadge color="primary">+ {change.to}</EuiBadge>{" "}
                  {change.from && (
                    <span>
                      {translate(`${i18n}.tags.kept`, {
                        values: { tags: change.from },
                        defaultMessage: "(keeps {{tags}})",
                      })}
                    </span>
                  )}
                </EuiText>
              ) : (
                <EuiText size="xs" key={change.field}>
                  <strong>{change.field}</strong>:{" "}
                  <span style={{ textDecoration: "line-through" }}>
                    {change.from ??
                      translate(`${i18n}.unset`, { defaultMessage: "unset" })}
                  </span>{" "}
                  → <strong>{change.to}</strong>
                </EuiText>
              ),
            )}
            {device.warning && (
              <EuiText size="xs" color="warning">
                {device.warning}
              </EuiText>
            )}
          </>
        );
      },
    },
  ];

  const work = networkWork(plan?.network);
  // What Apply would actually write. The device half alone used to gate the
  // button, so an appliance whose device records were conform could never have
  // its bonds, addresses or cables written - the one case where the cabling is
  // missing is exactly the case where the button was disabled.
  const totalWork = (plan?.changeCount ?? 0) + work.pending;

  const networkColumns: Array<EuiBasicTableColumn<OpsanioSyncNetworkAction>> = [
    {
      name: translate(`${i18n}.network.column.step`, {
        defaultMessage: "Addressing & cabling",
      }),
      render: (entry: OpsanioSyncNetworkAction) => (
        <EuiText size="xs">{entry.action}</EuiText>
      ),
    },
    {
      name: translate(`${i18n}.network.column.state`, {
        defaultMessage: "State",
      }),
      width: "170px",
      render: (entry: OpsanioSyncNetworkAction) =>
        !entry.ok ? (
          <EuiText size="xs" color="danger">
            {entry.error ??
              translate(`${i18n}.network.blocked`, {
                defaultMessage: "blocked",
              })}
          </EuiText>
        ) : entry.existed ? (
          <EuiBadge color="hollow">
            {translate(`${i18n}.network.existing`, {
              defaultMessage: "already in place",
            })}
          </EuiBadge>
        ) : (
          <EuiBadge color="primary">
            {translate(`${i18n}.network.pending`, {
              defaultMessage: "to create",
            })}
          </EuiBadge>
        ),
    },
  ];

  const networkActions = plan?.network
    ? NETWORK_GROUPS.flatMap((group) => plan.network?.[group] ?? [])
    : [];

  const networkSection = plan?.network ? (
    <>
      <EuiSpacer size="m" />
      <EuiText size="xs" color="subdued">
        <strong>
          {translate(`${i18n}.network.title`, {
            values: {
              pending: work.pending,
              existing: work.existing,
              blocked: work.blocked.length,
            },
            defaultMessage:
              "ADDRESSING & CABLING - {{pending}} to create, {{existing}} already in place, {{blocked}} blocked",
          })}
        </strong>
      </EuiText>
      <EuiSpacer size="s" />

      {plan.network.warnings.length > 0 && (
        <>
          <EuiCallOut
            size="s"
            color="warning"
            iconType="alert"
            title={translate(`${i18n}.network.warnings`, {
              defaultMessage: "The plan could not cover everything",
            })}
          >
            <ul>
              {plan.network.warnings.map((warning) => (
                <li key={warning}>
                  <EuiText size="xs">{warning}</EuiText>
                </li>
              ))}
            </ul>
          </EuiCallOut>
          <EuiSpacer size="s" />
        </>
      )}

      {networkActions.length === 0 ? (
        <EuiCallOut
          size="s"
          color="warning"
          iconType="questionInCircle"
          title={translate(`${i18n}.network.empty`, {
            defaultMessage:
              "Nothing to address or cable - no CPE-tagged NIC or no switch selected",
          })}
        />
      ) : (
        <EuiBasicTable<OpsanioSyncNetworkAction>
          items={networkActions}
          columns={networkColumns}
          tableLayout="auto"
          compressed
        />
      )}
    </>
  ) : null;

  return (
    <>
      <EuiButton
        size="s"
        iconType="wrench"
        onClick={preview}
        isLoading={planning}
      >
        {translate(`${i18n}.button`, {
          defaultMessage: "Sync to NetBox",
        })}
      </EuiButton>

      {error && !plan && (
        <>
          <EuiSpacer size="s" />
          <EuiCallOut size="s" color="danger" iconType="alert" title={error} />
        </>
      )}

      {plan && (
        <EuiModal onClose={() => setPlan(null)} style={{ minWidth: 640 }}>
          <EuiModalHeader>
            <EuiModalHeaderTitle>
              <h2>
                {translate(`${i18n}.modal.title`, {
                  values: { appliance: plan.appliance },
                  defaultMessage: "Sync {{appliance}} hardware to NetBox",
                })}
              </h2>
            </EuiModalHeaderTitle>
          </EuiModalHeader>
          <EuiModalBody>
            <EuiText size="s">
              <p>
                {translate(`${i18n}.modal.body`, {
                  defaultMessage:
                    "This re-runs the questionnaire's NetBox workflow: assignment tags, the conventional device name and site/tenant, plus the node bonds, their addresses and the cabling to the switches. Nothing has been written yet.",
                })}
              </p>
            </EuiText>

            {error && (
              <>
                <EuiSpacer size="s" />
                <EuiCallOut
                  size="s"
                  color="danger"
                  iconType="alert"
                  title={error}
                />
              </>
            )}

            {plan.missing.length > 0 && (
              <>
                <EuiSpacer size="s" />
                <EuiCallOut
                  size="s"
                  color="warning"
                  iconType="questionInCircle"
                  title={translate(`${i18n}.missing`, {
                    values: { ids: plan.missing.join(", ") },
                    defaultMessage:
                      "NetBox does not know these selected devices: {{ids}} - they are skipped",
                  })}
                />
              </>
            )}

            <EuiSpacer size="m" />
            {plan.changeCount === 0 ? (
              <EuiCallOut
                size="s"
                color="success"
                iconType="check"
                title={translate(`${i18n}.nothing`, {
                  defaultMessage:
                    "Every assigned device already matches the conventions",
                })}
              />
            ) : (
              <EuiBasicTable<OpsanioSyncDevicePlan>
                items={plan.devices}
                columns={changeColumns}
                tableLayout="auto"
              />
            )}

            {networkSection}
          </EuiModalBody>
          <EuiModalFooter>
            <EuiButtonEmpty onClick={() => setPlan(null)}>
              {translate("anketa.create.action.cancel", {
                defaultMessage: "Cancel",
              })}
            </EuiButtonEmpty>
            <EuiButton
              fill
              color="warning"
              iconType="wrench"
              onClick={apply}
              isLoading={applying}
              isDisabled={totalWork === 0}
            >
              {translate(`${i18n}.apply`, {
                values: { count: totalWork },
                defaultMessage: "Apply {{count}} changes",
              })}
            </EuiButton>
          </EuiModalFooter>
        </EuiModal>
      )}
    </>
  );
};

export default HardwareSyncButton;
